// The 4 Scene Controller — DeviceID 101.
//
// A touch panel with FOUR buttons and NO outputs: pressing button N means "run
// scene N of the room I am in". It has no relays to report and nothing of its
// own to control; its whole job is to trigger the room's scenes, which live on
// the Wall tables (docs/07-scenes.md) — button N is the Nth scene of the room
// in the room's own order, so the owner picks which scene sits on which button
// by ordering the room's scenes.
//
// ── Who executes ───────────────────────────────────────────────────────────
//
// Until this device existed a scene ran INSIDE THE APP: the phone read the step
// list and published each command. A wall panel has no app to publish through,
// so the server now runs scenes (`POST /api/RunScene`, api/new/sceneRunner.js):
// the panel calls it on a press, and the app calls it on a tap, so both go
// through one executor and a scene runs exactly once however it was started.
//
// ⚠️ `runSceneEverywhere` falls back to publishing from the app ONLY when the
// endpoint is not there (an older backend answers with an HTML 404, or the
// network is down). A real answer from the server — 403 not permitted, 404 no
// such scene, 409 already running — is final and is surfaced, never worked
// around: publishing locally after a 403 would be a permission bypass.

import { runSceneOnServer } from '../api/api';
import { publishCommand } from '../utils/deviceController';

export const SCENE_CONTROLLER_DEVICE_ID = 101;
export const SCENE_BUTTONS = 4;

export const isSceneController = (deviceId) => Number(deviceId) === SCENE_CONTROLLER_DEVICE_ID;

/**
 * The scene number in a press report — "Scene 1", "SCENE 1", "scene-2",
 * "Scene3" — or null for anything else (a status, a wifi list, an ack).
 */
export function parseScenePress(status) {
  const m = /^\s*scene\s*[-_ ]?\s*(\d{1,2})\s*$/i.exec(String(status ?? ''));
  if (!m) return null;
  const n = Number(m[1]);
  return n >= 1 ? n : null;
}

// Delay between consecutive scene messages when the app has to publish them
// itself. Firmware/broker cannot take a burst fired back-to-back.
export const SCENE_STEP_DELAY_MS = 350;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** The old executor: publish every step from here, spaced out. */
export async function runSceneLocally(userId, scene) {
  const steps = (scene.steps || []).filter((s) => s.uniqueNumber && s.message);
  for (let i = 0; i < steps.length; i++) {
    publishCommand(steps[i].uniqueNumber, steps[i].message, userId);
    if (i < steps.length - 1) await sleep(SCENE_STEP_DELAY_MS); // eslint-disable-line no-await-in-loop
  }
  return { ok: true, via: 'app', steps: steps.length };
}

/** A reply that came from sceneRunner, not from a router that never heard of it. */
const isRunnerReply = (data) => data && typeof data === 'object' && typeof data.ok === 'boolean';

/**
 * Run a scene: server first, app only when the server has no RunScene.
 * -> { ok, via: 'server' | 'app', steps?, error?, status? }
 * A server refusal resolves with ok:false — it does not throw and it does not
 * fall back.
 */
export async function runSceneEverywhere(userId, scene) {
  const id = scene && (scene.id ?? scene.WallButtonId);
  if (id != null) {
    try {
      const res = await runSceneOnServer({ WallButtonId: id, UserId: userId });
      if (isRunnerReply(res.data)) return { ...res.data, via: 'server' };
    } catch (e) {
      const data = e && e.response && e.response.data;
      if (isRunnerReply(data)) return { ...data, via: 'server', status: e.response.status };
      // No JSON reply at all — the endpoint is not deployed, or we are offline.
    }
  }
  return runSceneLocally(userId, scene);
}

/** Scene N of a controller, by unique number — what a press does, from the app. */
export async function runControllerScene(userId, uniqueNumber, sceneNo) {
  const res = await runSceneOnServer({ UniqueNumber: uniqueNumber, SceneNo: sceneNo, UserId: userId })
    .catch((e) => (e && e.response) || { data: null });
  const data = res && res.data;
  if (isRunnerReply(data)) return { ...data, via: 'server' };
  return { ok: false, via: 'server', error: 'RunScene is not available on the server' };
}
